import { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';
import apiService from '../services/api';
import { useAuth } from '../auth/AuthProvider';

export interface FeatureFlags {
  [key: string]: boolean;
}

export interface UseFeatureFlagsReturn {
  flags: FeatureFlags;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isEnabled: (featureName: string) => boolean;
}

// Default flags - always available
const DEFAULT_FLAGS = {
  'feature.email.campaigns': true,
  'feature.linkedin.campaigns': false,
  'feature.google.campaigns': false,
  'feature.meta.campaigns': false,
  'feature.lead.scoring': true,
  'feature.reports.advanced': false
};

export const useFeatureFlags = (): UseFeatureFlagsReturn => {
  const { user, loading: authLoading } = useAuth();
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFlags = useCallback(async () => {
    // Don't fetch if auth is still loading or user is not authenticated
    if (authLoading || !user) {
      logger.debug('Using default flags - no auth or auth loading');
      setFlags(DEFAULT_FLAGS);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiService.getFeatureFlags();
      setFlags(data);
    } catch (err: any) {
      logger.error('Failed to fetch feature flags:', err);
      setError(err.message || "We couldn't load feature flags. Using defaults.");
      // Keep default flags on error
      setFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  const refresh = useCallback(async () => {
    await fetchFlags();
  }, [fetchFlags]);

  const isEnabled = useCallback((featureName: string): boolean => {
    return flags[featureName] ?? false;
  }, [flags]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  return {
    flags,
    loading,
    error,
    refresh,
    isEnabled
  };
};